/**
 * In-memory fixture store for the admin demo build (`vue-cli-service build --mode demo`).
 * Shapes mirror what Dashboard / CreateSurveyForm expect from the real API.
 * Mutations persist for the browser session only; reload calls resetState().
 */

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function makeSubstanceModes(substanceId, startId, modes) {
  const rows = []
  let id = startId
  for (const mode of modes) {
    for (const dataType of ["Binary", "Numeric"]) {
      rows.push({
        id: id++,
        substanceId,
        name: mode.name,
        dataType,
        redcapFieldName: mode.redcapFieldName
      })
    }
  }
  return { modes: rows, nextId: id }
}

function buildInitialState() {
  let nextId = 1

  const user = {
    id: 1,
    username: "demo.user",
    firstName: "Demo",
    lastName: "User",
    email: "demo@example.com",
    isAdmin: false,
    usableByApi: true
  }

  const substanceDefs = [
    { name: "Alcohol", modes: [{ name: "All Types", redcapFieldName: "alc" }, { name: "Beer", redcapFieldName: "beer" }, { name: "Wine", redcapFieldName: "wine" }, { name: "Liquor", redcapFieldName: "liq" }] },
    { name: "Cigarettes", modes: [{ name: "All Types", redcapFieldName: "cig" }] },
    { name: "E-Cigarettes", modes: [{ name: "All Types", redcapFieldName: "ecig" }] },
    { name: "Cannabis", modes: [{ name: "All Types", redcapFieldName: "can" }, { name: "Smoked", redcapFieldName: "cansmk" }, { name: "Edible", redcapFieldName: "canedb" }, { name: "Vaped", redcapFieldName: "canvpe" }] },
    { name: "Stimulants", modes: [{ name: "All Types", redcapFieldName: "stim" }] },
    { name: "Hallucinogens", modes: [{ name: "All Types", redcapFieldName: "halluc" }] },
    { name: "Opioids", modes: [{ name: "All Types", redcapFieldName: "opioid" }] },
    { name: "Other", modes: [{ name: "All Types", redcapFieldName: "other" }, { name: "Custom", redcapFieldName: "other" }] }
  ]

  const substances = substanceDefs.map((def) => {
    const substanceId = nextId++
    const { modes, nextId: afterModes } = makeSubstanceModes(substanceId, nextId, def.modes)
    nextId = afterModes
    return {
      id: substanceId,
      name: def.name,
      substanceModes: modes
    }
  })

  const projectId = nextId++
  const tokenId = nextId++

  return {
    nextId,
    user,
    users: [user],
    substances,
    redcapApiTokens: [
      {
        id: tokenId,
        userId: user.id,
        token: "demo-redcap-token",
        project: {
          id: projectId,
          name: "Demo REDCap Project",
          publicId: "00000000-0000-4000-8000-000000000001",
          otlfbSurvey: null
        }
      }
    ],
    projectInfoByProjectId: {
      [projectId]: {
        project_title: "Demo REDCap Project",
        in_production: 0
      }
    },
    fieldnames: [
      { export_field_name: "record_id" },
      { export_field_name: "demo_field" }
    ],
    nextRecord: 1
  }
}

let state = buildInitialState()

export function resetState() {
  state = buildInitialState()
}

export function getState() {
  return state
}

export function allocateId() {
  return state.nextId++
}

/** Deep-clone helper for API responses so Vue sees fresh objects after mutations. */
export function snapshot(value) {
  return clone(value)
}

export function seedDemoSession() {
  const { user } = state
  const payload = {
    id: user.id,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    isAdmin: user.isAdmin,
    usableByApi: user.usableByApi
  }
  /* Minimal three-segment JWT shape so any decodeJwtPayload callers succeed. */
  const header = btoa(JSON.stringify({ alg: "none", typ: "JWT" }))
  const body = btoa(JSON.stringify(payload))
  const token = `${header}.${body}.demo`
  localStorage.setItem("access-token", token)
  localStorage.setItem("carl-user", JSON.stringify(payload))
}

export default {
  resetState,
  getState,
  allocateId,
  snapshot,
  seedDemoSession
}
